import { Injectable } from '@angular/core';
import { AuthDto } from '@lib/user/utils';
import { BaseResponse } from '@shared/utils/interfaces';
import { finalize, firstValueFrom, from, map, Observable, of, shareReplay } from 'rxjs';

import { REFRESH_LOCK_KEY } from './user-token-state.constant';

@Injectable({
  providedIn: 'root',
})
export class UserTokenStateService {
  // Holds the single in-flight refresh for this tab. Concurrent callers subscribe to the same
  // stream so a successful refresh is shared and a failed one propagates the error to everyone
  // (no waiter is left hanging). Reset to null once the refresh settles.
  private refresh$: Observable<string | null> | null = null;

  getIsRefreshing(): boolean {
    return this.refresh$ !== null;
  }

  waitUntilRefreshEnds(): Observable<string | null> {
    return this.refresh$ ?? of(null);
  }

  /**
   * Serializes token refresh across browser tabs via the Web Locks API and de-duplicates
   * concurrent refreshes within a single tab via the shared in-flight stream.
   *
   * @param refreshFn         performs the actual refresh request.
   * @param resolveFreshToken runs inside the lock; when another tab has already refreshed it
   *        must sync local state and return the fresh token so we can skip a redundant request.
   *        Returns `null` when a refresh is still needed.
   */
  triggerRefresh(refreshFn: () => Observable<BaseResponse<AuthDto>>, resolveFreshToken?: () => string | null): Observable<string | null> {
    if (this.refresh$) {
      return this.refresh$;
    }

    const runInsideLock = (): Observable<string | null> => {
      const freshToken = resolveFreshToken?.() ?? null;
      if (freshToken !== null) {
        return of(freshToken);
      }

      return refreshFn().pipe(map(({ payload }) => payload.accessToken));
    };

    // `LockGrantedCallback<T>` is typed as returning `T`, so TypeScript infers `T = Promise<string | null>`
    // and types `request()` as `Promise<Promise<string | null>>`. Awaiting flattens it back to the
    // `string | null` that actually resolves at runtime, which keeps `source` a single Observable type.
    const requestUnderLock = async (locks: LockManager): Promise<string | null> => {
      const token = await locks.request(REFRESH_LOCK_KEY, () => firstValueFrom(runInsideLock()));

      return token;
    };

    const lockManager = typeof navigator !== 'undefined' ? navigator.locks : undefined;
    const source: Observable<string | null> = lockManager ? from(requestUnderLock(lockManager)) : runInsideLock();

    this.refresh$ = source.pipe(
      finalize(() => {
        this.refresh$ = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    return this.refresh$;
  }
}
